import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { useAnalyticsContext } from '../analytics-context';
import { format, subMinutes } from 'date-fns';

const WINDOW_OPTIONS = [
  { label: '5m', value: 5 },
  { label: '15m', value: 15 },
  { label: '30m', value: 30 },
  { label: '6h', value: 360 },
  { label: '12h', value: 720 },
  { label: '24h', value: 1440 },
] as const;

const VIEW_OPTIONS = [
  { key: 'realtime', label: 'Realtime' },
  { key: 'today', label: 'Today' },
] as const;

type ViewMode = typeof VIEW_OPTIONS[number]['key'];

const GaActiveUsers: React.FC = () => {
  const { ga, activeUsersHistoryRange, loadActiveUsersHistory } =
    useAnalyticsContext();

  const realtimeState = ga.activeUsers?.realtime ?? {
    total: 0,
    timeline: [],
    error: null,
    isLoading: false,
  };
  const historyState = ga.activeUsers?.history ?? {
    timeline: [],
    summary: { total: 0, max: 0, average: 0 },
    granularity: null,
    isLoading: false,
    error: null,
  };

  const realtimeTimeline = useMemo(
    () => realtimeState.timeline ?? [],
    [realtimeState.timeline],
  );
  const hasRealtimeTimeline = realtimeTimeline.length > 0;

  const [windowMinutes, setWindowMinutes] = useState<number>(30);
  const [viewMode, setViewMode] = useState<ViewMode>('realtime');

  useEffect(() => {
    if (viewMode === 'realtime') {
      return;
    }
    const today = new Date();
    const range = {
      startDate: format(today, 'yyyy-MM-dd'),
      endDate: format(today, 'yyyy-MM-dd'),
    };

    if (
      !activeUsersHistoryRange ||
      activeUsersHistoryRange.startDate !== range.startDate ||
      activeUsersHistoryRange.endDate !== range.endDate
    ) {
      loadActiveUsersHistory(range);
    }
  }, [viewMode, loadActiveUsersHistory, activeUsersHistoryRange]);

  const chartDataRealtime = useMemo(() => {
    if (!hasRealtimeTimeline) {
      return [];
    }
    const now = new Date();
    return [...realtimeTimeline]
      .map(item => {
        const minutesAgo = Number(item.minute || 0);
        const label = format(subMinutes(now, minutesAgo), 'HH:mm');
        return {
          ...item,
          minutesAgo,
          label,
        };
      })
      .sort((a, b) => a.minutesAgo - b.minutesAgo)
      .filter(item => item.minutesAgo < windowMinutes);
  }, [hasRealtimeTimeline, realtimeTimeline, windowMinutes]);

  const historyTimeline = useMemo(
    () => historyState.timeline ?? [],
    [historyState.timeline],
  );

  const chartDataHistory = useMemo(() => {
    return historyTimeline.map(point => {
      const date = new Date(point.timestamp);
      const label =
        historyState.granularity === 'day'
          ? format(date, 'MMM d')
          : format(date, 'HH:mm');
      return {
        ...point,
        label,
      };
    });
  }, [historyTimeline, historyState.granularity]);

  if (!ga.integration.isConfigured) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">Active Users</CardTitle>
        </CardHeader>
        <CardContent className="py-6 text-sm text-muted-foreground">
          {ga.integration.message ||
            'Connect Google Analytics to view active users.'}
        </CardContent>
      </Card>
    );
  }

  const realtimeError = realtimeState.error as any;
  if (
    viewMode === 'realtime' &&
    realtimeError &&
    realtimeError.status &&
    realtimeError.status !== 400
  ) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">Active Users</CardTitle>
        </CardHeader>
        <CardContent className="py-6 text-sm text-red-600">
          {realtimeError.data?.message ||
            realtimeError.message ||
            'Failed to load realtime data.'}
        </CardContent>
      </Card>
    );
  }

  if (viewMode === 'realtime' && realtimeState.isLoading) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">Active Users</CardTitle>
        </CardHeader>
        <CardContent className="py-6">
          <div className="h-40 w-full animate-pulse rounded-xl bg-muted/50" />
        </CardContent>
      </Card>
    );
  }

  if (viewMode === 'realtime' && !hasRealtimeTimeline) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">Active Users</CardTitle>
        </CardHeader>
        <CardContent className="py-6 text-sm text-muted-foreground">
          No realtime activity detected right now.
        </CardContent>
      </Card>
    );
  }

  const historyError = historyState.error as any;
  if (
    viewMode !== 'realtime' &&
    historyError &&
    historyError.status &&
    historyError.status !== 400
  ) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">Active Users</CardTitle>
        </CardHeader>
        <CardContent className="py-6 text-sm text-red-600">
          {historyError.data?.message ||
            historyError.message ||
            'Failed to load historical data.'}
        </CardContent>
      </Card>
    );
  }

  if (viewMode !== 'realtime' && historyState.isLoading) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">Active Users</CardTitle>
        </CardHeader>
        <CardContent className="py-6">
          <div className="h-40 w-full animate-pulse rounded-xl bg-muted/50" />
        </CardContent>
      </Card>
    );
  }

  if (viewMode !== 'realtime' && chartDataHistory.length === 0) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">Active Users</CardTitle>
        </CardHeader>
        <CardContent className="py-6 text-sm text-muted-foreground">
          No historical activity available for this range.
        </CardContent>
      </Card>
    );
  }

  const isRealtimeMode = viewMode === 'realtime';
  const currentRealtimePoint =
    chartDataRealtime.find(point => point.minutesAgo === 0) ??
    chartDataRealtime[0];
  const currentActiveRealtime = currentRealtimePoint
    ? currentRealtimePoint.activeUsers
    : realtimeState.total;

  const displayValue = isRealtimeMode
    ? currentActiveRealtime
    : historyState.summary.max;
  const displayLabel = isRealtimeMode
    ? 'Currently active users'
    : 'Peak active users';

  return (
    <Card className="border-none shadow-sm">
      <CardHeader className="pb-2 flex flex-wrap items-center justify-between gap-3">
        <CardTitle className="text-lg font-semibold">Active Users</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          {VIEW_OPTIONS.map(option => (
            <Button
              key={option.key}
              variant={viewMode === option.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode(option.key)}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm text-muted-foreground">{displayLabel}</p>
            <p className="text-2xl font-semibold text-foreground">
              {displayValue.toLocaleString()}
            </p>
            {!isRealtimeMode && (
              <p className="text-xs text-muted-foreground">
                Avg:{' '}
                {historyState.summary.average.toLocaleString(undefined, {
                  maximumFractionDigits: 1,
                })}{' '}
                · Total:{' '}
                {historyState.summary.total.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })}
              </p>
            )}
          </div>
          {isRealtimeMode && (
            <div className="flex flex-wrap items-center gap-2">
              {WINDOW_OPTIONS.map(option => (
                <Button
                  key={option.value}
                  variant={
                    windowMinutes === option.value ? 'default' : 'outline'
                  }
                  size="sm"
                  onClick={() => setWindowMinutes(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          )}
        </div>

        <div className="h-[240px]">
          <ResponsiveContainer>
            <LineChart
              data={isRealtimeMode ? chartDataRealtime : chartDataHistory}
              margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
            >
              <defs>
                <linearGradient
                  id="activeUsersGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="4 4"
                className="stroke-muted/50"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                className="text-xs text-muted-foreground"
              />
              <YAxis
                tickMargin={8}
                axisLine={false}
                tickLine={false}
                className="text-xs text-muted-foreground"
                tickFormatter={value =>
                  value.toLocaleString(undefined, { maximumFractionDigits: 0 })
                }
              />
              <Tooltip
                formatter={(value: number, key: string) => [
                  value.toLocaleString(undefined, {
                    maximumFractionDigits: 0,
                  }),
                  key === 'events' ? 'Events' : 'Active users',
                ]}
              />
              <Line
                type="monotone"
                dataKey="activeUsers"
                stroke="#2563eb"
                strokeWidth={2}
                dot={false}
                name="Active Users"
                fill="url(#activeUsersGradient)"
                strokeLinecap="round"
                isAnimationActive
                animationDuration={800}
              />
              {isRealtimeMode && (
                <Line
                  type="monotone"
                  dataKey="events"
                  stroke="#f97316"
                  strokeWidth={1.5}
                  dot={false}
                  strokeDasharray="4 6"
                  name="Events"
                  isAnimationActive
                  animationDuration={800}
                  opacity={0.7}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default GaActiveUsers;
